import { FC, useCallback } from 'react';

import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, InputNumber, Select, Space } from 'antd';

import { generateUniqueID } from '../../../../../helpers/utils/functions';
import InputWithColorPreview from '../../../../../shared/common/components/reports/create-mertrics/InputWithColorPreview';
import { ColorRule } from '../../../types';

export interface ColorRulesEditorProps {
  rules: ColorRule[];
  onChange: (rules: ColorRule[]) => void;
}

const fontWeightOptions = [
  { value: 400, label: 'Обычный' },
  { value: 500, label: 'Средний' },
  { value: 600, label: 'Полужирный' },
  { value: 700, label: 'Жирный' },
  { value: 800, label: 'Очень жирный' },
  { value: 900, label: 'Сверхжирный' },
];

/** Дефолтные стили числового блока */
const DEFAULT_FONT_SIZE = 27;
const DEFAULT_FONT_WEIGHT = 600;
const DEFAULT_COLOR = '#000000';

const EMPTY_RULE: ColorRule = {
  id: '_empty',
  from: null,
  to: null,
  color: DEFAULT_COLOR,
  fontSize: DEFAULT_FONT_SIZE,
  fontWeight: DEFAULT_FONT_WEIGHT,
};

const createNewRule = (overrides?: Partial<ColorRule>): ColorRule => ({
  id: generateUniqueID(),
  from: null,
  to: null,
  color: DEFAULT_COLOR,
  fontSize: DEFAULT_FONT_SIZE,
  fontWeight: DEFAULT_FONT_WEIGHT,
  ...overrides,
});

const ColorRulesEditor: FC<ColorRulesEditorProps> = ({ rules, onChange }) => {
  const handleAdd = useCallback(() => {
    onChange([ ...rules, createNewRule() ]);
  }, [ rules, onChange ]);

  const handleRemove = useCallback((id: string) => {
    onChange(rules.filter((r) => r.id !== id));
  }, [ rules, onChange ]);

  const handleClear = useCallback((id: string) => {
    onChange(rules.map((r) => r.id === id
      ? { ...r, from: null, to: null, color: DEFAULT_COLOR, fontSize: DEFAULT_FONT_SIZE, fontWeight: DEFAULT_FONT_WEIGHT }
      : r
    ));
  }, [ rules, onChange ]);

  const handleUpdate = useCallback((id: string, field: string, val: any) => {
    onChange(
      rules.map((r) => (r.id === id ? { ...r, [field]: val } : r))
    );
  }, [ rules, onChange ]);

  const handleEmptyEdit = useCallback((overrides: Partial<ColorRule>) => {
    onChange([ createNewRule(overrides) ]);
  }, [ onChange ]);

  // При первом рендере показываем хотя бы одну строку
  const displayRules = rules.length > 0 ? rules : [ EMPTY_RULE ];

  return (
    <div style={{ marginTop: '16px', marginBottom: '8px' }}>
      <div style={{ fontWeight: '600', marginBottom: '12px' }}>
        Условия стиля числа
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}>
        {displayRules.map((rule, index) => {
          const isEmpty = rule.id === '_empty';

          return (
            <div
              key={rule.id}
              style={{ display: 'flex', columnGap: '10px', alignItems: 'center', flexWrap: 'wrap' }}
            >
              от
              <InputNumber
                style={{ width: '100px' }}
                value={rule.from}
                onChange={(val) => {
                  if (isEmpty) {
                    handleEmptyEdit({ from: val as number | null });
                  } else {
                    handleUpdate(rule.id, 'from', val);
                  }
                }}
              />
              до
              <InputNumber
                style={{ width: '100px' }}
                value={rule.to}
                onChange={(val) => {
                  if (isEmpty) {
                    handleEmptyEdit({ to: val as number | null });
                  } else {
                    handleUpdate(rule.id, 'to', val);
                  }
                }}
              />
              <InputWithColorPreview
                value={rule.color}
                onChange={(val: string) => {
                  if (isEmpty) {
                    handleEmptyEdit({ color: val });
                  } else {
                    handleUpdate(rule.id, 'color', val);
                  }
                }}
              />
              <InputNumber
                style={{ width: '80px' }}
                min={12}
                max={120}
                value={rule.fontSize}
                placeholder="px"
                onChange={(val) => {
                  if (isEmpty) {
                    handleEmptyEdit({ fontSize: val ?? undefined });
                  } else {
                    handleUpdate(rule.id, 'fontSize', val ?? undefined);
                  }
                }}
              />
              <Select
                style={{ width: '140px' }}
                value={rule.fontWeight}
                placeholder="Жирность"
                allowClear
                options={fontWeightOptions}
                onChange={(val) => {
                  if (isEmpty) {
                    handleEmptyEdit({ fontWeight: val ?? undefined });
                  } else {
                    handleUpdate(rule.id, 'fontWeight', val ?? undefined);
                  }
                }}
              />
              {!isEmpty && index === 0 && (
                <CloseOutlined onClick={() => handleClear(rule.id)} style={{ cursor: 'pointer' }} />
              )}
              {!isEmpty && index !== 0 && (
                <CloseOutlined onClick={() => handleRemove(rule.id)} style={{ cursor: 'pointer' }} />
              )}
            </div>
          );
        })}
      </div>
      <Space style={{ marginTop: '16px' }}>
        <span style={{ fontWeight: '600' }}>Задать условия</span>
        <Button
          onClick={handleAdd}
          icon={<PlusOutlined />}
          type="primary"
          shape="circle"
        />
      </Space>
    </div>
  );
};

export default ColorRulesEditor;
